import BN from 'bignumber.js';

import {
  STAKE_SETTINGS_ROSN,
  STAKE_DETAILS_ROSN,
  STAKE_SEND_ROSN,
  STAKE_SUCCESS_ROSN,
  STAKE_ERROR_ROSN,
  RESET_STAKE_ROSN,
  STAKE_BALANCE_ROSN,
  UNSTAKED_BALANCE_ROSN,
  UNSTAKE_TIME_ROSN,
  UNSTAKE_SETTINGS_ROSN,
  UNSTAKE_INFORMATION_ROSN,
  UNSTAKE_DETAILS_ROSN,
  UNSTAKE_IMMEDIATELY_DETAILS_ROSN,
  ESTIMATED_DAILY_REWARDS_ROSN,
  APR_ROSN,
  UNSTAKE_SEND_ROSN,
  UNSTAKE_SUCCESS_ROSN,
  WITHDRAW_DETAILS_ROSN,
  WITHDRAW_SEND_ROSN,
  WITHDRAW_SUCCESS_ROSN,
  HISTORY_SHOW_ROSN_STAKING,
  EARNED_REWARDS_BALANCE_ROSN,
  CLAIMING_FEE_PERCENT_ROSN,
} from './action-types';
import { getRosnBalance, approve } from './token-actions';
import rosnStakingContractService from '../services/contracts/rosn-staking-contract-service';
import web3Service from '../services/web3-service';
import { ROSN } from '../constants/blockchain-constants';
import { getTotalStakedBalance } from './staking-actions';
import config from '../config/env';
import { calculateStakeBalance, getPrice } from '../helpers/stake-helpers';
import { getAllPricesLogs, getAllTransfersLogs } from '../services/thegraph.staking.service';

export const updateBalances = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const [stakeBalance, price, unstakes] = await Promise.all([
    rosnStakingContractService.balanceOf(address),
    getPrice(rosnStakingContractService),
    rosnStakingContractService.getUnstake(address),
    dispatch(getRosnBalance()),
  ]);

  const { amount, applicableAt } = unstakes;

  const calculatedStakeBalance = calculateStakeBalance(stakeBalance, price);

  // TODO: make one dispatch
  dispatch({
    type: STAKE_BALANCE_ROSN,
    payload: {
      stakeBalance: Number.isNaN(Number(calculatedStakeBalance)) ? '0' : calculatedStakeBalance,
    },
  });
  dispatch({ type: UNSTAKE_TIME_ROSN, payload: { unstakedTime: applicableAt } });
  dispatch({ type: UNSTAKED_BALANCE_ROSN, payload: { unstakedBalance: amount } });
};

const estimatedBlocksIntervalStrategyRewards = (fromTimestamp, toTimestamp, strategy) => {
  const strategyFromTimestamp = Math.max(fromTimestamp, strategy[2]);
  const strategyToTimestamp = Math.max(toTimestamp, strategy[0]);

  if (strategyToTimestamp <= strategyFromTimestamp) {
    return '0';
  }

  return strategy[1] * (strategyToTimestamp - strategyFromTimestamp);
};

const estimatedBlocksIntervalRewards = async (
  fromTimestamp,
  toTimestamp,
  currentStrategy,
  nextStrategy,
) => {
  let result = estimatedBlocksIntervalStrategyRewards(fromTimestamp, toTimestamp, currentStrategy);
  result += estimatedBlocksIntervalStrategyRewards(fromTimestamp, toTimestamp, nextStrategy);
  return result;
};

const estimatedAPR = async () => {
  const [totalStaked, fromBlock, currentStrategy, nextStrategy] = await Promise.all([
    rosnStakingContractService.totalStaked(),
    web3Service.web3.eth.getBlockNumber(),
    rosnStakingContractService.currentStrategy(),
    rosnStakingContractService.nextStrategy(),
  ]);

  const { timestamp: fromTimestamp } = await web3Service.web3.eth.getBlock(fromBlock);

  const totalStakedNumber = new BN(totalStaked).toString(10);
  if (totalStakedNumber === '0') {
    return Infinity;
  }

  const toTimestamp = Math.max(currentStrategy.endsAt, nextStrategy.endsAt);
  if (toTimestamp <= fromTimestamp) {
    return '0';
  }

  const period = toTimestamp - fromTimestamp;
  const yearBlockPeriod = 3600 * 24 * 365;
  const yearBlocksCount = yearBlockPeriod / period;
  const estimatedBlocks = await estimatedBlocksIntervalRewards(
    fromTimestamp,
    toTimestamp,
    currentStrategy,
    nextStrategy,
  );
  const yearRewards = yearBlocksCount * estimatedBlocks;
  return (yearRewards / totalStaked) * 100;
};

const dailyReward = async (address) => {
  const [balanceOf, totalSupply, currentStrategy] = await Promise.all([
    rosnStakingContractService.balanceOf(address),
    rosnStakingContractService.totalSupply(),
    rosnStakingContractService.currentStrategy(),
  ]);

  if (totalSupply === '0') {
    return '0';
  }

  const dayBlockPeriod = 3600 * 24;
  return currentStrategy.perSecondReward * dayBlockPeriod * (balanceOf / totalSupply);
};

// STAKE

export const stakeSettings = () => async (dispatch) => {
  dispatch({ type: STAKE_SETTINGS_ROSN });
};

export const stakeDetails = (formValues) => async (dispatch) => {
  dispatch({ type: STAKE_DETAILS_ROSN, payload: formValues });
};

export const stakeSend = (formValues) => async (dispatch, getState) => {
  try {
    const { address } = getState().account;
    dispatch({ type: STAKE_SEND_ROSN });
    await approve(ROSN, address, config.REACT_APP_ROSN_STAKING_ADDRESS);
    const { amount } = formValues;
    const transaction = await rosnStakingContractService.stake(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: STAKE_SUCCESS_ROSN, payload: { id: transaction.transactionHash } });
  } catch (e) {
    dispatch({ type: STAKE_ERROR_ROSN, payload: { error: 'Stake failed' } });
  }
};

export const unstakeSettings = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_SETTINGS_ROSN });
};

export const unstakeInformation = (formValues) => async (dispatch) => {
  dispatch({ type: UNSTAKE_INFORMATION_ROSN, payload: formValues });
};

export const unstakeDetails = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_DETAILS_ROSN });
};

export const unstakeImmediatelyDetails = () => async (dispatch) => {
  dispatch({ type: UNSTAKE_IMMEDIATELY_DETAILS_ROSN });
};

export const unstakeSend = (formValues) => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: UNSTAKE_SEND_ROSN });

  const { amount } = formValues;

  try {
    const transaction = await rosnStakingContractService.unstake(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: UNSTAKE_SUCCESS_ROSN, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR_ROSN, payload: { error: 'Unstake failed' } });
  }
};

export const unstakeImmediatelySend = (formValues) => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: UNSTAKE_SEND_ROSN });

  const { amount } = formValues;

  try {
    const transaction = await rosnStakingContractService.claim(address, amount);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: UNSTAKE_SUCCESS_ROSN, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR_ROSN, payload: { error: 'Unstake failed' } });
  }
};

export const withdrawUnstakedDetails = () => async (dispatch) => {
  dispatch({ type: WITHDRAW_DETAILS_ROSN });
};

export const withdrawUnstakedSend = () => async (dispatch, getState) => {
  const { address } = getState().account;

  dispatch({ type: WITHDRAW_SEND_ROSN });

  try {
    const transaction = await rosnStakingContractService.withdraw(address);
    await dispatch(updateBalances());

    // COMPLETE
    dispatch({ type: WITHDRAW_SUCCESS_ROSN, payload: { id: transaction.transactionHash } });
  } catch (error) {
    dispatch({ type: STAKE_ERROR_ROSN, payload: { error: 'Unstake failed' } });
  }
};

export const stakeReset = () => async (dispatch) => {
  dispatch({ type: RESET_STAKE_ROSN });
};

export const showHistory = () => async (dispatch) => {
  dispatch({ type: HISTORY_SHOW_ROSN_STAKING });
};

export const getStakedBalance = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const [stakeBalance, price] = await Promise.all([
    rosnStakingContractService.balanceOf(address),
    getPrice(rosnStakingContractService),
  ]);

  const calculatedStakeBalance = calculateStakeBalance(stakeBalance, price);

  dispatch({
    type: STAKE_BALANCE_ROSN,
    payload: {
      stakeBalance: Number.isNaN(Number(calculatedStakeBalance)) ? '0' : calculatedStakeBalance,
    },
  });
  dispatch(getTotalStakedBalance());
};

export const getUnstakes = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const { amount, applicableAt } = await rosnStakingContractService.getUnstake(address);
  dispatch({ type: UNSTAKE_TIME_ROSN, payload: { unstakedTime: applicableAt } });
  dispatch({ type: UNSTAKED_BALANCE_ROSN, payload: { unstakedBalance: amount } });
};

export const getClaimingFeePercent = () => async (dispatch, getState) => {
  const data = await rosnStakingContractService.getClaimingFeePercent();
  dispatch({ type: CLAIMING_FEE_PERCENT_ROSN, payload: data });
};

export const getEstimatedAPR = () => async (dispatch) => {
  const apr = await estimatedAPR();
  dispatch({ type: APR_ROSN, payload: { apr: Number(apr).toFixed(2) } });
};

export const getEstimatedDailyRewards = () => async (dispatch, getState) => {
  const { address } = getState().account;

  const estimatedDailyRewards = await dailyReward(address);

  dispatch({
    type: ESTIMATED_DAILY_REWARDS_ROSN,
    payload: { estimatedDailyRewards: `${estimatedDailyRewards}` },
  });
};

export const getEarnedRewards = () => async (dispatch, getState) => {
  const { address } = getState().account;
  const LogType = {
    SENDED: 'SENDED',
    RECEIVED: 'RECEIVED',
  };

  const transfersLogs = await getAllTransfersLogs('rosn');
  const pricesLogs = await getAllPricesLogs('rosn');

  const receivingLogs = transfersLogs.filter((item) => item.to === address);
  const sendingLogs = transfersLogs.filter((item) => item.from === address);

  const blockPrices = new Map();

  pricesLogs.forEach((log) => {
    blockPrices.set(log.blockNumber, new BN(log.mantissa));
  });

  const logs = [...receivingLogs, ...sendingLogs]
    .map(({ from, blockNumber, value, logIndex }) => {
      const type = from === address ? LogType.SENDED : LogType.RECEIVED;
      return {
        type,
        block: blockNumber,
        value: new BN(value),
        index: logIndex,
      };
    })
    .sort((a, b) => {
      if (a.block < b.block) return -1;
      if (a.block > b.block) return 1;
      return a.index - b.index;
    });

  let balance = new BN(0);
  let staked = new BN(0);
  let reward = new BN(0);

  logs.forEach((log) => {
    const { type, value, block } = log;
    const price = blockPrices.get(block);
    if (!price) throw new Error(`Unable to get price of block #${block}`);
    if (type === LogType.RECEIVED) {
      staked = staked.plus(value.times(price).div(1e18));
      balance = balance.plus(value);
    } else {
      reward = reward.plus(balance.times(price).div(1e18).minus(staked.plus(reward)));
      const sended = value.times(price).div(1e18);
      if (reward.lt(sended)) {
        staked = staked.minus(sended.minus(reward));
        reward = new BN(0);
      } else reward = reward.minus(sended);
      balance = balance.minus(value);
    }
  });
  const currentPrice = await getPrice(rosnStakingContractService);

  const notLockedReward = balance.times(currentPrice).minus(staked.plus(reward));
  const totalReward = reward.plus(notLockedReward);

  dispatch({
    type: EARNED_REWARDS_BALANCE_ROSN,
    payload: { earnedRewards: totalReward.toFixed(0) },
  });
};

export const initData = () => async (dispatch) =>
  Promise.all([
    dispatch(getUnstakes()),
    dispatch(getStakedBalance()),
    dispatch(getEstimatedAPR()),
    dispatch(getEstimatedDailyRewards()),
    dispatch(getClaimingFeePercent()),
    dispatch(getEarnedRewards()),
    dispatch(updateBalances()),
  ]);
